import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';

import Box from '@mui/material/Box';
import { ThemeProvider } from '@mui/material/styles';

import ViewPage from '../layouts/ViewPage';
import { Tab, Tabs } from '@mui/material';
import insightsTheme from '../theme/insightsTheme';
import './pricing-reports/design/design.scss';
import PropTypes from 'prop-types';

import ContestInsights from '../components/Insights/Contests';
import BeefInsights from '../components/Insights/BeefInsights';
import RecipesInsights from '../components/Insights/RecipesInsights';
import PageViewInsights from '../components/Insights/PageViewInsights';
import ReportsWithFilter from '../components/Analytics/ReportsWithFilter';
import { PricingReportsPanel } from './pricing-reports';
import { FlyerReportPanel } from './flyer-report';
import { VerifyPricePanel } from './verify-price';
import { canViewPricingReports } from '../common/utils/pricingReportsAccess';
import { insightsDesignEnabled, insightsSectionClassName } from '../common/utils/insightsDesignEnabled';

function LinkTab(props) {
  return (
    <Tab
      component={Link}
      aria-current={props.selected && 'page'}
      {...props}
    />
  );
}

LinkTab.propTypes = {
  selected: PropTypes.bool,
};

const Insights = () => {
  const params = useParams();
  const user = useSelector((s) => s.user);
  const showPricing = canViewPricingReports(user);

  const { tabSlugs, defaultSlug } = useMemo(
    () =>
      showPricing
        ? {
            tabSlugs: ['pricing-reports', 'verify-price', 'reports', 'beef', 'recipes'],
            defaultSlug: 'pricing-reports',
          }
        : {
            tabSlugs: ['reports', 'beef', 'recipes'],
            defaultSlug: 'reports',
          },
    [showPricing]
  );

  const slug = params.slug || defaultSlug;
  const tabIndex = tabSlugs.indexOf(slug);
  const tabsValue = tabIndex >= 0 ? tabIndex : false;

  const breadcrumbLabel = slug.replace(/-/g, ' ');
  const breadcrumbSlug = params.slug || defaultSlug;

  const insightsPage = (
    <Box
      className={insightsDesignEnabled() ? 'insights-design-root' : undefined}
      sx={{ flexGrow: 1 }}
    >
      <ViewPage
        title="Insights"
        description="Pricing reports, analytics, and page-view trends for your gateway."
        breadcrumbsClassName={insightsSectionClassName('insights-section--chrome')}
        breadcrumbsLinks={[
          {
            url: '/insights',
            content: 'Insights',
          },
          {
            url: `/insights/${breadcrumbSlug}`,
            content: breadcrumbLabel,
          }
        ]}
        prefix='View in '
      >
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={tabsValue}
            aria-label="Insights sections"
            variant="scrollable"
            scrollButtons="auto"
          >
            {showPricing && (
              <LinkTab label="Pricing Reports" to="/insights/pricing-reports" />
            )}
            {showPricing && (
              <LinkTab label="Verify Price" to="/insights/verify-price" />
            )}
            <LinkTab label="Reports" to="/insights/reports" />
            <LinkTab label="Beef" to="/insights/beef" />
            <LinkTab label="Recipes" to="/insights/recipes" />
          </Tabs>
        </Box>

        <Box sx={{ maxWidth: 1440, mx: 'auto', width: '100%' }}>
          {
            slug === 'contests' ? <ContestInsights /> :
            slug === 'beef' ? <BeefInsights /> :
            slug === 'recipes' ? <RecipesInsights /> :
            slug === 'reports' ? <ReportsWithFilter /> :
            slug === 'pricing-reports' ? <PricingReportsPanel /> :
            slug === 'verify-price' ? <VerifyPricePanel /> :
            slug === 'flyer-report' ? <FlyerReportPanel /> :
            slug === 'overview' ? <PageViewInsights /> : null
          }
        </Box>
      </ViewPage>
    </Box>
  );

  return insightsDesignEnabled() ? (
    <ThemeProvider theme={insightsTheme}>{insightsPage}</ThemeProvider>
  ) : (
    insightsPage
  );
}

export default Insights;
